import { getAccessToken } from '../authentication/supabaseService';
import { RevenueSource } from '../../types/RevenueSource';

const API_BASE = '/api/admin';

export type RevenueAutomationRunStatus = 'success' | 'failure';

export type RevenueAutomationConfig = Record<string, unknown>;

export interface RevenueAutomationClient {
  id: string;
  name: string;
  revenueSource: RevenueSource;
  loginEmail: string;
  loginUrl: string | null;
  isEnabled: boolean;
  hasPassword: boolean;
  config: RevenueAutomationConfig;
  lastRunAt: string | null;
  lastRunStatus: RevenueAutomationRunStatus | null;
  lastRunError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClientInput {
  name: string;
  revenueSource: RevenueSource;
  loginEmail: string;
  loginPassword: string;
  loginUrl?: string | null;
  isEnabled?: boolean;
  config?: RevenueAutomationConfig;
}

export interface UpdateClientInput {
  name?: string;
  revenueSource?: RevenueSource;
  loginEmail?: string;
  // Empty string / omitted means "keep existing".
  loginPassword?: string;
  loginUrl?: string | null;
  isEnabled?: boolean;
  config?: RevenueAutomationConfig;
}

export interface RunClientResult {
  success: boolean;
  error?: string;
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  return fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) detail = body.error;
    } catch {
      // ignore parse failure, keep statusText
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

export async function listRevenueAutomationClients(): Promise<RevenueAutomationClient[]> {
  const res = await fetchWithAuth('/revenue-automation-clients');
  return jsonOrThrow<RevenueAutomationClient[]>(res);
}

export async function createRevenueAutomationClient(input: CreateClientInput): Promise<RevenueAutomationClient> {
  const res = await fetchWithAuth('/revenue-automation-clients', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrThrow<RevenueAutomationClient>(res);
}

export async function updateRevenueAutomationClient(id: string, input: UpdateClientInput): Promise<RevenueAutomationClient> {
  const res = await fetchWithAuth(`/revenue-automation-clients/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return jsonOrThrow<RevenueAutomationClient>(res);
}

export async function deleteRevenueAutomationClient(id: string): Promise<void> {
  const res = await fetchWithAuth(`/revenue-automation-clients/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete: ${res.statusText}`);
}

export async function runRevenueAutomationClient(id: string): Promise<RunClientResult> {
  const res = await fetchWithAuth(`/revenue-automation-clients/${id}/run`, { method: 'POST' });
  return jsonOrThrow<RunClientResult>(res);
}

export interface BackprocessResult {
  started: boolean;
  startDate: string;
  // The server caps the end date at yesterday — this is the effective end used.
  endDate: string;
  dayCount: number;
}

export async function backprocessRevenueAutomationClient(
  id: string,
  startDate: string,
  endDate: string,
): Promise<BackprocessResult> {
  const res = await fetchWithAuth(`/revenue-automation-clients/${id}/backprocess`, {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate }),
  });
  return jsonOrThrow<BackprocessResult>(res);
}
